"use client";

import { useState } from "react";
import type { FC, HTMLAttributes } from "react";
import {
	Button,
	Col as DSCol,
	type ColProps,
	Grid,
	Heading,
	Icon,
	Image,
	Text
} from "@pamperedchef/design-system";
import type { Cta, CmsImage } from "@/composites/landing/types";
import { VideoModal } from "@/composites/foundation";
import * as styles from "./VideoCallout.module.scss";

const Col = DSCol as FC<ColProps & HTMLAttributes<HTMLDivElement>>;

export interface VideoCalloutProps {
	title?: string;
	body?: string;
	posterImage?: CmsImage;
	videoUrl?: string;
	cta?: Cta;
}

/**
 * Video feature callout — title, body, and an optional CTA next to a media
 * frame. When `videoUrl` is provided the frame plays via DS `VideoPlayer`
 * (poster + native controls); otherwise it falls back to the DS `Image`
 * poster with a play affordance that follows the CTA href.
 * the CTA open a modal that plays the video (DS `Modal`, size `xl`).
 * The modalMirrors the Figma intent for the VIP-kit landing page (still play poster, click to
 * open the video).
 */
export function VideoCallout({
	title,
	body,
	posterImage,
	videoUrl,
	cta
}: VideoCalloutProps) {
	const [open, setOpen] = useState(false);
	const playAriaLabel = cta?.title ?? "Play video";

	return (
		<section
			className={styles["video-callout"]}
			data-component="VideoCallout"
		>
			<Grid
				columns={11}
				gap={0}
				className={styles["video-callout__grid"]}
			>
				<Col
					size={11}
					lg={5}
					className={styles["video-callout__media"]}
				>
					{posterImage ? (
						<Image
							src={posterImage.url}
							alt={posterImage.alt ?? title ?? "Video poster"}
							className={styles["video-callout__poster"]}
							objectFit="cover"
						/>
					) : (
						<div
							className={styles["video-callout__poster-fallback"]}
							aria-hidden="true"
						/>
					)}
					<button
						type="button"
						className={styles["video-callout__play"]}
						aria-label={playAriaLabel}
						onClick={() => setOpen(true)}
					>
						<Icon name="play" size={28} aria-hidden />
					</button>
				</Col>

				<Col
					size={11}
					lg={6}
					className={styles["video-callout__content"]}
				>
					{title || body ? (
						<div className={styles["video-callout__text"]}>
							{title ? (
								<Heading
									tag="h2"
									className={styles["video-callout__title"]}
								>
									{title}
								</Heading>
							) : null}
							{body ? (
								<Text
									tag="p"
									styledAs="mdr"
									className={styles["video-callout__body"]}
								>
									{body}
								</Text>
							) : null}
						</div>
					) : null}
					{cta ? (
						<Button
							kind="primary"
							size="lg"
							onPress={() => setOpen(true)}
							className={styles["video-callout__cta"]}
						>
							{cta.title}
						</Button>
					) : null}
				</Col>
			</Grid>

			<VideoModal
				open={open}
				onClose={() => setOpen(false)}
				title={title}
				videoUrl={videoUrl}
				poster={posterImage?.url}
			/>
		</section>
	);
}
